import { createContext, useContext, useMemo } from "react";
import { useGamesQuery, useSportGamesQuery } from "./utils";
import { ConfigContext } from "./ConfigProvider";
import { LobbyConfigContext } from "./LobbyConfigProvider";

export interface Game {
  id: string;
  name: string;
  extGameId: string;
  providerName: string;
  extProviderId: string;
  providerDisplayName: string;
  isLive?: boolean;
  displayName?: string;
  image?: string;
  link?: string;
  category?: string;
}

export interface CompactGame {
  i: number; // id
  eid: string; // extGameId
  n: string; // name
  l: boolean; // isLive
  p: string; // providerName
  pn?: string; // providerDisplayName
  dn?: string; // displayName
  epid?: string; // extProviderId
  img?: string; // image
  lk?: string; // link
  c?: string; // category
}

export interface SportGame {
  id: string;
  sportType: string;
  sportDisplayName: string;
  tournament: string;
  tournamentDisplayName: string;
  image?: string;
  link?: string;
  isLive?: boolean;
}

export interface CompactSportGame {
  i: number; // id
  st: string; // sportType
  dn?: string; // sport displayName
  t: string; // tournament
  tdn?: string; // tournamentDisplayName
  img?: string; // image
  lk?: string; // link
  l: boolean; // isLive
}

export interface GameIndexes {
  byId: Record<string, Game>;
  byExtGameId: Record<string, Game>;
  byProvider: Record<string, Game[]>;
  allProviders: string[];
  filterGamesByExtIds: (extGameIds: string[], providerNames?: string[]) => Game[];
  getGameById: (id: string) => Game | undefined;
  getGameByExtId: (extGameId: string) => Game | undefined;
  getGamesByProvider: (providerName: string) => Game[];
}

export interface SportGameIndexes {
  bySportType: Record<string, SportGame[]>;
  byTournament: Record<string, SportGame[]>;
  allSportTypes: string[];
  filterSportGamesByConditions: (sportNames?: string[], tournamentNames?: string[]) => SportGame[];
}

export interface GamesContextType {
  games: Game[];
  sportGames: SportGame[];
  indexes: GameIndexes;
  sportGameIndexes: SportGameIndexes;
}

export const GamesContext = createContext<GamesContextType>({
  games: [],
  sportGames: [],
  indexes: {
    byId: {},
    byExtGameId: {},
    byProvider: {},
    allProviders: [],
    filterGamesByExtIds: () => [],
    getGameById: () => undefined,
    getGameByExtId: () => undefined,
    getGamesByProvider: () => [],
  },
  sportGameIndexes: {
    bySportType: {},
    byTournament: {},
    allSportTypes: [],
    filterSportGamesByConditions: () => [],
  },
});

const ALL_FILTER = "all";

const toFilterKey = (value: string | undefined) =>
  String(value || "")
    .trim()
    .toLowerCase();

const hasFilterMatch = (filterSet: Set<string>, values: Array<string | undefined>) => {
  for (const value of values) {
    if (filterSet.has(toFilterKey(value))) return true;
  }
  return false;
};

const isAllFilter = (values?: string[]) =>
  !values?.length || values.some((value) => toFilterKey(value) === ALL_FILTER);

export const GamesProvider = ({ children }: { children: React.ReactNode }) => {
  const promoConfig = useContext<any>(ConfigContext);
  const lobbyConfig = useContext<any>(LobbyConfigContext);

  const orgUuid = promoConfig?.orgUuid || lobbyConfig?.orgUuid;

  const { data = [] } = useGamesQuery(orgUuid);
  const { data: sportGames = [] } = useSportGamesQuery(orgUuid);

  // a game toggled IsLive=false in the backoffice must not be offered/displayed in the
  // widget or FS exchange; a missing flag is treated as live (older catalog data)
  const liveGames = useMemo(() => data.filter((game: Game) => game.isLive !== false), [data]);

  const gameIndexes = useMemo(() => {
    const byId: Record<string, Game> = {};
    const byExtGameId: Record<string, Game> = {};
    const byProvider: Record<string, Game[]> = {};
    const byProviderId: Record<string, Game[]> = {};
    const providerSet = new Set<string>();

    // index every game by id so historical references (e.g. an already-won game) resolve
    data.forEach((game: Game) => {
      byId[game.id] = game;
      byExtGameId[game.extGameId] = game;
    });

    // provider buckets only hold live games — these drive what is offered/displayed
    liveGames.forEach((game: Game) => {
      const providerName = game.providerName || game.extProviderId;
      if (providerName) {
        if (!byProvider[providerName]) {
          byProvider[providerName] = [];
        }
        byProvider[providerName].push(game);
        providerSet.add(providerName);
      }
      if (game.extProviderId) {
        if (!byProviderId[game.extProviderId]) {
          byProviderId[game.extProviderId] = [];
        }
        byProviderId[game.extProviderId].push(game);
      }
    });

    const allProviders = Array.from(providerSet);

    // Utility functions leveraging indexes — only return live games (offered/displayed)
    const filterGamesByExtIds = (extGameIds: string[], providerNames?: string[]): Game[] => {
      if (!extGameIds?.length) return [];

      if (extGameIds[0] === "all" && providerNames?.[0] === "all") {
        return liveGames;
      }

      if (extGameIds[0] === "all") {
        if (providerNames?.length) {
          // new promos pass provider ids, legacy promos pass names
          return providerNames.flatMap(
            (provider) => byProvider[provider] || byProviderId[provider] || []
          );
        }
        return liveGames;
      }

      return extGameIds
        .map((extId) => byExtGameId[extId])
        .filter((game): game is Game => Boolean(game) && game.isLive !== false);
    };

    const getGameById = (id: string): Game | undefined => byId[id];
    const getGameByExtId = (extGameId: string): Game | undefined => byExtGameId[extGameId];
    const getGamesByProvider = (providerName: string): Game[] => byProvider[providerName] || [];

    return {
      byId,
      byExtGameId,
      byProvider,
      allProviders,
      filterGamesByExtIds,
      getGameById,
      getGameByExtId,
      getGamesByProvider,
    };
  }, [data, liveGames]);

  const sportGameIndexes = useMemo(() => {
    const bySportType: Record<string, SportGame[]> = {};
    const byTournament: Record<string, SportGame[]> = {};
    const sportTypeSet = new Set<string>();

    sportGames.forEach((sportGame: SportGame) => {
      const sportTypeKey = toFilterKey(sportGame.sportType);
      const tournamentKey = toFilterKey(sportGame.tournament);

      if (sportTypeKey) {
        if (!bySportType[sportTypeKey]) {
          bySportType[sportTypeKey] = [];
        }
        bySportType[sportTypeKey].push(sportGame);
        sportTypeSet.add(sportGame.sportType);
      }

      if (tournamentKey) {
        if (!byTournament[tournamentKey]) {
          byTournament[tournamentKey] = [];
        }
        byTournament[tournamentKey].push(sportGame);
      }
    });

    const filterSportGamesByConditions = (
      sportNames?: string[],
      tournamentNames?: string[]
    ): SportGame[] => {
      const shouldShowAllSports = isAllFilter(sportNames);
      const shouldShowAllTournaments = isAllFilter(tournamentNames);
      const sportNameSet = new Set((sportNames || []).map(toFilterKey));
      const tournamentNameSet = new Set((tournamentNames || []).map(toFilterKey));

      return sportGames.filter((sportGame: SportGame) => {
        const sportMatches =
          shouldShowAllSports ||
          hasFilterMatch(sportNameSet, [sportGame.sportType, sportGame.sportDisplayName]);
        const tournamentMatches =
          shouldShowAllTournaments ||
          hasFilterMatch(tournamentNameSet, [
            sportGame.tournament,
            sportGame.tournamentDisplayName,
          ]);

        return sportMatches && tournamentMatches;
      });
    };

    return {
      bySportType,
      byTournament,
      allSportTypes: Array.from(sportTypeSet),
      filterSportGamesByConditions,
    };
  }, [sportGames]);

  const contextValue = useMemo(
    () => ({
      games: liveGames,
      sportGames,
      indexes: gameIndexes,
      sportGameIndexes,
    }),
    [liveGames, sportGames, gameIndexes, sportGameIndexes]
  );

  return <GamesContext.Provider value={contextValue}>{children}</GamesContext.Provider>;
};

export const useGames = (): Game[] => {
  const { games } = useContext(GamesContext);
  return games;
};

export const useGameIndexes = (): GameIndexes => {
  const { indexes } = useContext(GamesContext);
  return indexes;
};

export const useSportGames = (): SportGame[] => {
  const { sportGames } = useContext(GamesContext);
  return sportGames;
};

export const useSportGameIndexes = (): SportGameIndexes => {
  const { sportGameIndexes } = useContext(GamesContext);
  return sportGameIndexes;
};
